import { plural, t } from "@bezbolisno/i18n";
import { can } from "@bezbolisno/kit/rbac";
import { Badge } from "@bezbolisno/ui/badge";
import { Card } from "@bezbolisno/ui/card";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { PageHeader } from "../../../components/page-header";
import { Pager } from "../../../components/pager";
import { TaskItem } from "../../../components/task-item";
import { TaskSearchSchema, tasksQuery } from "../../../screens/tasks/search";
import { TaskFilters } from "../../../screens/tasks/task-filters";

export const Route = createFileRoute("/_authed/tasks/")({
  validateSearch: TaskSearchSchema,
  // Managers never see tasks (no task:manage) — bounce like the Next page did; the API enforces it too.
  beforeLoad: ({ context }) => {
    if (!can(context.session.role, "task:manage")) throw redirect({ to: "/" });
  },
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(tasksQuery(deps)),
  component: TasksPage,
});

function TasksPage() {
  const search = Route.useSearch();
  const { data, isError } = useQuery(tasksQuery(search));

  return (
    <div className="fade-in">
      <PageHeader
        title={t.tasksPage.title}
        badge={
          data ? (
            <Badge tone={data.total > 0 ? "info" : "neutral"}>
              {data.total} {plural(data.total, t.tasksPage.resultCount)}
            </Badge>
          ) : null
        }
      />

      {isError || !data ? (
        <Card className="p-5 text-sm text-[color:var(--muted)]">{t.analytics.dbError}</Card>
      ) : (
        <>
          <TaskFilters search={search} summary={data.summary} assignees={data.assignees} />
          {data.items.length === 0 ? (
            <Card className="p-5 text-sm text-[color:var(--muted)]">{t.tasksPage.empty}</Card>
          ) : (
            <div className="list">
              {data.items.map((task) => (
                <TaskItem key={task.id} task={task} assignees={data.assignees} linkToPatient />
              ))}
            </div>
          )}
          {data.total > data.filter.pageSize ? (
            <Pager
              basePath="/tasks"
              sp={search}
              page={data.filter.page}
              pageSize={data.filter.pageSize}
              total={data.total}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
